import Link from 'next/link';
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Button, Col, Row } from 'react-bootstrap';

import cx from 'classnames';

import CrossReferenceBannerSectionCampaignContent from 'components/CrossReferenceBannerSectionCampaignContent/CrossReferenceBannerSectionCampaignContent';
import DonationOptionGrid from 'components/DonationOptionGrid/DonationOptionGrid';
import FestiveBaubleDonationOptionsForm, {
  IFestiveBaubleDonationOptionsFormValues,
} from 'components/FestiveBaubleDonationOptionsForm/FestiveBaubleDonationOptionsForm';
import SectionWithContainer from 'components/PageBlocks/SectionWithContainer/SectionWithContainer';
import RowWithOffsetCol from 'components/RowWithOffsetCol/RowWithOffsetCol';
import { useApp } from 'contexts/appContext';
import { CartContext } from 'contexts/cartContext';
import { IDonationCampaign } from 'models/donationCampaign';
import { IDonationOption } from 'models/donationOption';
import { Frequency } from 'models/frequency';
import Layouts from 'models/layouts';
import { DonationCampaignPageService } from 'services/donationCampaignService';
import { fetcher } from 'services/httpRequestor';

interface IFestiveBaubleDonationCampaignPageProps {
  donationCampaign: IDonationCampaign;
}
const FestiveBaubleDonationCampaignPage: React.FC<
  IFestiveBaubleDonationCampaignPageProps
> = ({ donationCampaign }) => {
  const {
    actions: { goToCheckoutPage },
  } = useContext(CartContext);
  const { featuredGiftGivingCampaignSlug } = useApp();
  const [featuredGiftGivingCampaign, setFeaturedGiftGivingCampaign] = useState<
    IDonationCampaign | undefined
  >();
  const [relatedDonationCampaignOptions, setRelatedDonationCampaignOptions] =
    useState<IDonationOption[]>([]);

  const [loading, setLoading] = useState(false);

  const defaultSelectedDonationOptionID = useMemo(
    () =>
      donationCampaign.donation_options?.find(
        donationOption => donationOption.default_ind,
      )?.id,
    [donationCampaign.donation_options],
  );

  const [selectedDonationOptionID, setSelectedDonationOptionID] = useState(
    defaultSelectedDonationOptionID,
  );
  const selectedDonationOption = useMemo(
    () =>
      donationCampaign?.donation_options?.find(
        ({ id }) => id === selectedDonationOptionID,
      ),
    [donationCampaign.donation_options, selectedDonationOptionID],
  );

  useEffect(() => {
    const donationCampaignService = new DonationCampaignPageService(fetcher());
    if (featuredGiftGivingCampaignSlug) {
      donationCampaignService
        .getOneByPageName(featuredGiftGivingCampaignSlug)
        .then(({ data: featuredCampaign }) => {
          setFeaturedGiftGivingCampaign(featuredCampaign);

          const relatedDonationOptions =
            featuredCampaign?.donation_options
              ?.sort(() => (Math.random() < 0.5 ? 1 : -1))
              ?.slice(0, 4) || [];
          setRelatedDonationCampaignOptions(relatedDonationOptions);
        });
    }
  }, [featuredGiftGivingCampaignSlug]);

  const handlePurchase = useCallback(
    async (value: IFestiveBaubleDonationOptionsFormValues) => {
      setLoading(true);
      await goToCheckoutPage(
        {
          donation_campaign_id: donationCampaign.id,
          donation_option_id: value?.donation_option_id,
          custom_unit_amount: value.custom_unit_amount,
          quantity: 1,
          frequency: Frequency.OnceOff,
        },
        false,
        false,
      );
    },
    [donationCampaign.id, goToCheckoutPage],
  );

  const renderTitle = useCallback(() => {
    return (
      <div className="mb-3 d-flex flex-column">
        {donationCampaign.heading && (
          <h1 className="mr-2 mb-0 heading heading-danger">
            {donationCampaign.heading}
          </h1>
        )}
        {donationCampaign.sub_heading && (
          <h4 className={cx('mt-3 mb-0 font-weight-semibold')}>
            {donationCampaign.sub_heading}
          </h4>
        )}
      </div>
    );
  }, [donationCampaign.heading, donationCampaign.sub_heading]);

  const crossReferenceBannerCampaignContent = useMemo(() => {
    return featuredGiftGivingCampaign?.campaign_contents?.find(
      ({ layout }) => layout?.layout_type === Layouts.CROSS_REFERENCE_BANNER,
    );
  }, [featuredGiftGivingCampaign?.campaign_contents]);

  return (
    <>
      <SectionWithContainer>
        <div className="d-block d-md-none">{renderTitle()}</div>
        <Row>
          {selectedDonationOption?.image.url && (
            <Col className="mb-0" md={6}>
              <img
                className="w-100"
                src={selectedDonationOption.image.url}
                alt={selectedDonationOption.heading}
              />
            </Col>
          )}
          <Col md={6}>
            <div className="d-none d-md-block">{renderTitle()}</div>
            {donationCampaign.content_brief && (
              <div
                dangerouslySetInnerHTML={{
                  __html: donationCampaign.content_brief,
                }}
              />
            )}
            <h3 className="mb-3 mt-4 mt-sm-5">Choose your donation amount:</h3>
            <FestiveBaubleDonationOptionsForm
              loading={loading}
              defaultValues={
                defaultSelectedDonationOptionID
                  ? {
                      donation_option_id: defaultSelectedDonationOptionID,
                    }
                  : undefined
              }
              onChange={values => {
                setSelectedDonationOptionID(values.donation_option_id);
              }}
              onSubmit={handlePurchase}
              donationOptions={donationCampaign.donation_options}
            />
          </Col>
        </Row>
      </SectionWithContainer>
      {relatedDonationCampaignOptions?.length &&
        featuredGiftGivingCampaignSlug && (
          <SectionWithContainer>
            <RowWithOffsetCol className="mb-4">
              <h2 className="heading heading-danger mb-4">
                Other ways you can help
              </h2>
              <p>
                Our gift cards help to pay for the lifesaving equipment used by
                our volunteers.
              </p>
            </RowWithOffsetCol>
            <DonationOptionGrid
              donationCampaignOptions={relatedDonationCampaignOptions}
              donationCampaignSlug={featuredGiftGivingCampaignSlug}
            />
            <div className="d-flex justify-content-center">
              <Link href={featuredGiftGivingCampaignSlug}>
                <Button variant="danger" className="mt-3">
                  See more gifts
                </Button>
              </Link>
            </div>
          </SectionWithContainer>
        )}
      {crossReferenceBannerCampaignContent && (
        <CrossReferenceBannerSectionCampaignContent
          className="mt-5"
          campaignContent={crossReferenceBannerCampaignContent}
        />
      )}
    </>
  );
};

export default FestiveBaubleDonationCampaignPage;
